import * as React from 'react';

import styled, { css } from 'styled-components';

export interface ITableRowStyle {
  cursor?: string;
  borders?: 'none';
  /**
   * Marks the row as a navigation target (e.g. opens an entity's detail
   * page on click). Adds `cursor: pointer`, a hover background, and
   * keyboard affordance (Tab + Enter/Space) — so the list works on
   * touch devices and for keyboard-only operators.
   *
   * Sub-controls inside the row (buttons, toggles, inline links) must
   * `e.stopPropagation()` to keep their own clicks from also firing
   * the row's `onClick`.
   */
  clickable?: boolean;
}

export interface ITableRow extends ITableRowStyle {
  children: React.ReactNode[] | React.ReactNode;
  type?: RowType;
  onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTableRowElement>) => void;
  tabIndex?: number;
  role?: string;
  'aria-label'?: string;
}

export type RowType = 'row' | 'subRow' | 'subRowParent';

const StyledRow = styled.tr<ITableRowStyle & { type?: RowType }>`
  width: initial;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  box-sizing: inherit;
  cursor: ${({ cursor, clickable }) =>
    cursor || (clickable ? 'pointer' : undefined)};
  transition: background-color 0.12s;

  :after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    margin: 0 24px;
    height: 1px;
    width: calc(100% - 48px);
    border-bottom: 1px solid
      ${({ borders }) =>
        borders === 'none' ? 'transparent' : 'var(--k-border-ebeff2)'};
  }

  :last-child {
    border-bottom: 0;
  }
  background-color: ${({ type }) =>
    type === 'subRow' ? 'var(--k-bg-fafbfd)' : 'transparent'};
  box-shadow: ${({ type }) =>
    type === 'subRowParent' ? '0px 4px 8px var(--k-shadow-88959e-a80)' : ''};

  ${({ clickable }) =>
    clickable &&
    css`
      &:hover {
        background-color: var(--k-bg-f2f5f9);
      }
      &:focus-visible {
        outline: 2px solid var(--k-border-0d53fc);
        outline-offset: -2px;
      }
    `}
`;

/**
 * Row component with optional row-level navigation. When `clickable`
 * is set the row also responds to keyboard activation (Enter/Space) —
 * required for parity with the mouse path now that several entity
 * lists (Groups, Students, Teachers, Journals) navigate on row click.
 */
export const TableRow = React.memo<ITableRow>(
  ({ children, onClick, onKeyDown, clickable, tabIndex, role, ...rest }) => {
    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (onKeyDown) onKeyDown(e);
        if (!onClick || !clickable) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick((e as unknown) as React.MouseEvent<HTMLTableRowElement>);
        }
      },
      [onKeyDown, onClick, clickable],
    );

    return (
      <StyledRow
        {...rest}
        clickable={clickable}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        tabIndex={tabIndex ?? (clickable ? 0 : undefined)}
        role={role ?? (clickable ? 'button' : undefined)}
      >
        {children}
      </StyledRow>
    );
  },
);
